import React, { createContext, useContext, useReducer, useCallback, useState, useEffect } from 'react';

const AppContext = createContext({});

// Action types
const ActionTypes = {
  ADD_SHAPE: 'ADD_SHAPE',
  UPDATE_SHAPE: 'UPDATE_SHAPE',
  UPDATE_SHAPES: 'UPDATE_SHAPES',
  DELETE_SHAPE: 'DELETE_SHAPE',
  REMOVE_SHAPES: 'REMOVE_SHAPES',
  SELECT_SHAPE: 'SELECT_SHAPE',
  DESELECT_ALL: 'DESELECT_ALL',
  MERGE_SHAPES: 'MERGE_SHAPES',
  SET_MERGED_STRUCTURES: 'SET_MERGED_STRUCTURES',
  UNDO: 'UNDO',
  REDO: 'REDO',
  SET_TOOL: 'SET_TOOL',
  RESTORE_STATE: 'RESTORE_STATE',
  ADD_CONNECTION: 'ADD_CONNECTION',
  REMOVE_CONNECTION: 'REMOVE_CONNECTION',
  UPDATE_CONNECTIONS: 'UPDATE_CONNECTIONS',
  ADD_CLOSED_SHAPE: 'ADD_CLOSED_SHAPE',
  UPDATE_CLOSED_SHAPE: 'UPDATE_CLOSED_SHAPE',
  REMOVE_CLOSED_SHAPE: 'REMOVE_CLOSED_SHAPE',
  CLEAR_ALL_CLOSED_SHAPES: 'CLEAR_ALL_CLOSED_SHAPES'
};

// Storage keys for persistence
const STORAGE_KEYS = {
  APP_STATE: 'react-pixi-floor-planner-state',
  MERGED_STRUCTURES: 'react-pixi-floor-planner-merged-structures'
};

// Helper function to load state from localStorage
const loadPersistedState = () => {
  try {
    const savedState = localStorage.getItem(STORAGE_KEYS.APP_STATE);
    const savedMergedStructures = localStorage.getItem(STORAGE_KEYS.MERGED_STRUCTURES);
    
    if (savedState) {
      const parsedState = JSON.parse(savedState);
      
      // Use merged structures from main state if available, otherwise fall back to separate storage
      let mergedStructures = [];
      if (parsedState.mergedStructures) {
        mergedStructures = parsedState.mergedStructures;
        console.log('🔄 USING MERGED STRUCTURES FROM MAIN STATE');
      } else if (savedMergedStructures) {
        mergedStructures = JSON.parse(savedMergedStructures);
        console.log('🔄 USING MERGED STRUCTURES FROM SEPARATE STORAGE');
      }
      
      console.log('🎉 RESTORING COMPLETE PERSISTED STATE:', {
        shapesCount: parsedState.shapes?.length || 0,
        mergedStructuresCount: mergedStructures.length,
        mergedStructuresDetail: mergedStructures.map(ms => ({
          id: ms.id,
          shapesCount: ms.shapes?.length || 0,
          hasSnappedLines: ms.snappedSideLines?.length > 0,
          hasGapFillers: ms.gapFillerLines?.length > 0,
          hasDeletedTracking: Object.keys(ms.deletedSnappedLines || {}).length > 0,
          hasComplexData: !!(ms.snappedSideLines || ms.gapFillerLines || ms.deletedSnappedLines || ms.deletedGapFillers)
        })),
        historyCount: parsedState.history?.length || 0,
        lastSaved: new Date(parsedState.lastSaved || 0).toLocaleString()
      });
      
      return {
        ...parsedState,
        mergedStructures,
        // Reset non-persistent fields
        selectedShapeId: null,
        currentTool: null
      };
    }
  } catch (error) {
    console.error('❌ Failed to load persisted state:', error);
  }
  
  return null;
};

// Helper function to save state to localStorage with enhanced MergedStructure support
const saveStateToStorage = (state) => {
  try {
    // Create a deep copy of state to ensure all nested data is preserved
    const stateToSave = {
      shapes: JSON.parse(JSON.stringify(state.shapes)), // Deep copy shapes
      nextShapeId: state.nextShapeId,
      history: state.history.slice(-20), // Keep only last 20 history entries
      historyIndex: Math.min(state.historyIndex, 19), // Adjust index accordingly
      mergedStructures: JSON.parse(JSON.stringify(state.mergedStructures || [])), // Deep copy merged structures
      connections: JSON.parse(JSON.stringify(state.connections || [])), // Deep copy connections
      closedShapes: JSON.parse(JSON.stringify(state.closedShapes || [])), // Deep copy closed shapes
      lastSaved: Date.now()
    };
    
    // Save everything in one storage item to ensure consistency
    localStorage.setItem(STORAGE_KEYS.APP_STATE, JSON.stringify(stateToSave));
    
    // Also maintain separate merged structures backup for compatibility
    localStorage.setItem(STORAGE_KEYS.MERGED_STRUCTURES, JSON.stringify(state.mergedStructures || []));
    
    console.log('💾 ENHANCED STATE SAVED TO STORAGE:', {
      shapesCount: state.shapes.length,
      mergedStructuresCount: (state.mergedStructures || []).length,
      mergedStructuresDetail: (state.mergedStructures || []).map(ms => ({
        id: ms.id,
        shapesCount: ms.shapes?.length || 0,
        hasSnappedLines: ms.snappedSideLines?.length > 0,
        hasGapFillers: ms.gapFillerLines?.length > 0,
        hasDeletedTracking: Object.keys(ms.deletedSnappedLines || {}).length > 0
      })),
      timestamp: new Date().toLocaleTimeString()
    });
  } catch (error) {
    console.error('❌ Failed to save state to storage:', error);
  }
};

// Initial state with persistence check
const createInitialState = () => {
  const persistedState = loadPersistedState();
  
  if (persistedState) {
    return persistedState;
  }
  
  return {
    shapes: [],
    selectedShapeId: null,
    currentTool: null,
    history: [],
    historyIndex: -1,
    nextShapeId: 1,
    mergedStructures: [],
    // Connection tracking for EditableLines
    connections: [], // Array of { shapeId1, nodeIndex1, shapeId2, nodeIndex2 }
    // Closed shapes detection
    closedShapes: [] // Array of { id, lineIds: [], vertices: [], fillColor: string }
  };
};

const initialState = createInitialState();

// Reducer function
function appReducer(state, action) {
  let newState;
  
  switch (action.type) {
    case ActionTypes.ADD_SHAPE: {
 
      const newShape = {
        id: state.nextShapeId,
        ...action.payload,
        createdAt: Date.now()
      };
      
      newState = {
        ...state,
        shapes: [...state.shapes, newShape],
        nextShapeId: state.nextShapeId + 1
      };
      
      newState = addToHistory(newState, state);
      saveStateToStorage(newState); // Save after adding shape
      return newState;
    }
    
    case ActionTypes.UPDATE_SHAPE: {

      const { id, updates } = action.payload;
      
      // Handle selection updates (don't save to storage for selection changes)
      if (updates.selected !== undefined) {
        return {
          ...state,
          selectedShapeId: updates.selected ? id : null
        };
      }

      // Find the shape to update
      const shapeToUpdate = state.shapes.find(shape => shape.id === id);
      if (!shapeToUpdate) {
        console.warn('Shape not found:', id);
        return state;
      }

      // Create new shape with updates
      const updatedShape = {
        ...shapeToUpdate,
        ...updates,
        width: updates.width !== undefined ? Math.max(20, updates.width) : shapeToUpdate.width,
        height: updates.height !== undefined ? Math.max(20, updates.height) : shapeToUpdate.height,
        x: updates.x !== undefined ? updates.x : shapeToUpdate.x,
        y: updates.y !== undefined ? updates.y : shapeToUpdate.y
      };

      newState = {
        ...state,
        shapes: state.shapes.map(shape =>
          shape.id === id ? updatedShape : shape
        )
      };
      
      newState = addToHistory(newState, state);
      saveStateToStorage(newState); // Save after updating shape
      return newState;
    }
    
    case ActionTypes.UPDATE_SHAPES: {
      const { shapes } = action.payload;
      
      newState = {
        ...state,
        shapes: shapes
      };
      
      newState = addToHistory(newState, state);
      saveStateToStorage(newState); // Save after updating shapes
      return newState;
    }
    
    case ActionTypes.DELETE_SHAPE: {
      newState = {
        ...state,
        shapes: state.shapes.filter(shape => shape.id !== action.payload.id),
        selectedShapeId: state.selectedShapeId === action.payload.id ? null : state.selectedShapeId
      };
      
      newState = addToHistory(newState, state);
      saveStateToStorage(newState); // Save after deleting shape
      return newState;
    }
    
    case ActionTypes.REMOVE_SHAPES: {
      const { shapeIds } = action.payload;
      newState = {
        ...state,
        shapes: state.shapes.filter(shape => !shapeIds.includes(shape.id)),
        selectedShapeId: shapeIds.includes(state.selectedShapeId) ? null : state.selectedShapeId
      };
      
      console.log('🗑️ SHAPES REMOVED:', {
        removedShapeIds: shapeIds,
        remainingShapesCount: newState.shapes.length,
        timestamp: new Date().toLocaleTimeString()
      });
      
      newState = addToHistory(newState, state);
      saveStateToStorage(newState); // Save after removing shapes
      return newState;
    }
    
    case ActionTypes.SELECT_SHAPE:
      // Don't save selection changes to storage
      return {
        ...state,
        selectedShapeId: action.payload.id
      };
    
    case ActionTypes.DESELECT_ALL:
      // Don't save selection changes to storage
      return {
        ...state,
        selectedShapeId: null
      };
    
    case ActionTypes.MERGE_SHAPES: {
      // Implementation for merging shapes
      const { shapeIds, mergedShape } = action.payload;
      newState = {
        ...state,
        shapes: [
          ...state.shapes.filter(shape => !shapeIds.includes(shape.id)),
          { ...mergedShape, id: state.nextShapeId }
        ],
        nextShapeId: state.nextShapeId + 1,
        selectedShapeId: null
      };
      
      newState = addToHistory(newState, state);
      saveStateToStorage(newState); // Save after merging shapes
      return newState;
    }
    
    case ActionTypes.SET_TOOL:
      // Don't save tool changes to storage
      return {
        ...state,
        currentTool: action.payload.tool
      };
    
    case ActionTypes.SET_MERGED_STRUCTURES:
      console.log('🏗️ SETTING MERGED STRUCTURES:', {
        newCount: action.payload.mergedStructures?.length || 0,
        previousCount: state.mergedStructures?.length || 0,
        newStructuresDetail: (action.payload.mergedStructures || []).map(ms => ({
          id: ms.id,
          shapesCount: ms.shapes?.length || 0,
          hasComplexData: !!(ms.snappedSideLines || ms.gapFillerLines || ms.deletedSnappedLines)
        }))
      });
      
      newState = {
        ...state,
        mergedStructures: action.payload.mergedStructures
      };
      newState = addToHistory(newState, state); // Add to history for undo/redo
      saveStateToStorage(newState); // Save merged structures changes
      return newState;
    
    case 'DELETE_MERGED_STRUCTURE': {
      const newMergedStructures = state.mergedStructures 
        ? state.mergedStructures.filter(s => s.id !== action.payload.id)
        : [];
        
      console.log('🏗️ MERGED STRUCTURE DELETED:', {
        deletedId: action.payload.id,
        remainingStructures: newMergedStructures.length,
        timestamp: new Date().toLocaleTimeString()
      });
      
      newState = {
        ...state,
        mergedStructures: newMergedStructures,
        selectedShapeId: state.selectedShapeId === action.payload.id ? null : state.selectedShapeId
      };
      
      saveStateToStorage(newState); // Save after deleting merged structure
      return newState;
    }
    
    case ActionTypes.UNDO: {
      if (state.historyIndex > 0) {
        const previousState = state.history[state.historyIndex - 1];
        newState = {
          ...previousState,
          history: state.history,
          historyIndex: state.historyIndex - 1
        };
        saveStateToStorage(newState); // Save after undo
        return newState;
      }
      return state;
    }
    
    case ActionTypes.REDO: {
      if (state.historyIndex < state.history.length - 1) {
        const nextState = state.history[state.historyIndex + 1];
        newState = {
          ...nextState,
          history: state.history,
          historyIndex: state.historyIndex + 1
        };
        saveStateToStorage(newState); // Save after redo
        return newState;
      }
      return state;
    }
    
    case ActionTypes.RESTORE_STATE:
      console.log('🔄 RESTORING STATE FROM ACTION:', action.payload);
      return {
        ...state,
        ...action.payload
      };
    
    // Connection management
    case ActionTypes.ADD_CONNECTION: {
      const { shapeId1, nodeIndex1, shapeId2, nodeIndex2 } = action.payload;
      const newConnection = { shapeId1, nodeIndex1, shapeId2, nodeIndex2 };
      
      console.log('🔗 ADD_CONNECTION reducer processing:', {
        newConnection,
        currentConnections: state.connections,
        currentConnectionsCount: state.connections.length
      });
      
      // Check if connection already exists
      const connectionExists = state.connections.some(conn => 
        (conn.shapeId1 === shapeId1 && conn.nodeIndex1 === nodeIndex1 && 
         conn.shapeId2 === shapeId2 && conn.nodeIndex2 === nodeIndex2) ||
        (conn.shapeId1 === shapeId2 && conn.nodeIndex1 === nodeIndex2 && 
         conn.shapeId2 === shapeId1 && conn.nodeIndex2 === nodeIndex1)
      );
      
      console.log('🔗 Connection exists check:', connectionExists);
      
      if (!connectionExists) {
        newState = {
          ...state,
          connections: [...state.connections, newConnection]
        };
        
        console.log('🔗 Connection added, new state:', {
          newConnectionsCount: newState.connections.length,
          newConnections: newState.connections
        });
        
        newState = addToHistory(newState, state);
        saveStateToStorage(newState);
        return newState;
      } else {
        console.log('🔗 Connection already exists, skipping');
      }
      return state;
    }
    
    case ActionTypes.REMOVE_CONNECTION: {
      const { shapeId1, nodeIndex1, shapeId2, nodeIndex2 } = action.payload;
      newState = {
        ...state,
        connections: state.connections.filter(conn => 
          !((conn.shapeId1 === shapeId1 && conn.nodeIndex1 === nodeIndex1 && 
             conn.shapeId2 === shapeId2 && conn.nodeIndex2 === nodeIndex2) ||
            (conn.shapeId1 === shapeId2 && conn.nodeIndex1 === nodeIndex2 && 
             conn.shapeId2 === shapeId1 && conn.nodeIndex2 === nodeIndex1))
        )
      };
      newState = addToHistory(newState, state);
      saveStateToStorage(newState);
      return newState;
    }
    
    case ActionTypes.UPDATE_CONNECTIONS: {
      newState = {
        ...state,
        connections: action.payload.connections
      };
      newState = addToHistory(newState, state);
      saveStateToStorage(newState);
      return newState;
    }
    
    // Closed shape management
    case ActionTypes.ADD_CLOSED_SHAPE: {
      const newClosedShape = {
        id: `closed-${state.nextShapeId}`,
        ...action.payload
      };
      
      console.log('🎨 ADD_CLOSED_SHAPE reducer processing:', {
        newClosedShape,
        currentClosedShapesCount: state.closedShapes.length,
        currentClosedShapes: state.closedShapes
      });
      
      newState = {
        ...state,
        closedShapes: [...state.closedShapes, newClosedShape],
        nextShapeId: state.nextShapeId + 1
      };
      
      console.log('🎨 Closed shape added, new state:', {
        newClosedShapesCount: newState.closedShapes.length,
        newClosedShapes: newState.closedShapes
      });
      
      newState = addToHistory(newState, state);
      saveStateToStorage(newState);
      return newState;
    }
    
    case ActionTypes.UPDATE_CLOSED_SHAPE: {
      const { id, updates } = action.payload;
      newState = {
        ...state,
        closedShapes: state.closedShapes.map(shape => 
          shape.id === id ? { ...shape, ...updates } : shape
        )
      };
      newState = addToHistory(newState, state);
      saveStateToStorage(newState);
      return newState;
    }
    
    case ActionTypes.REMOVE_CLOSED_SHAPE: {
      const { id } = action.payload;
      newState = {
        ...state,
        closedShapes: state.closedShapes.filter(shape => shape.id !== id)
      };
      newState = addToHistory(newState, state);
      saveStateToStorage(newState);
      return newState;
    }
    
    case ActionTypes.CLEAR_ALL_CLOSED_SHAPES: {
      newState = {
        ...state,
        closedShapes: []
      };
      newState = addToHistory(newState, state);
      saveStateToStorage(newState);
      return newState;
    }
    
    default:
      return state;
  }
}

// Helper function to add state to history - now includes merged structures
function addToHistory(newState, currentState) {
  const historyEntry = {
    shapes: JSON.parse(JSON.stringify(currentState.shapes)), // Deep copy shapes
    selectedShapeId: currentState.selectedShapeId,
    nextShapeId: currentState.nextShapeId,
    mergedStructures: JSON.parse(JSON.stringify(currentState.mergedStructures || [])), // Deep copy merged structures
    connections: JSON.parse(JSON.stringify(currentState.connections || [])), // Deep copy connections
    closedShapes: JSON.parse(JSON.stringify(currentState.closedShapes || [])) // Deep copy closed shapes
  };
  
  const newHistory = [
    ...newState.history.slice(0, newState.historyIndex + 1),
    historyEntry
  ];
  
  // Limit history to 50 entries
  if (newHistory.length > 50) {
    newHistory.shift();
  } else {
    newState.historyIndex++;
  }
  
  console.log('📚 ADDED TO HISTORY:', {
    shapesCount: historyEntry.shapes.length,
    mergedStructuresCount: historyEntry.mergedStructures.length,
    historyIndex: newState.historyIndex,
    totalHistoryEntries: newHistory.length
  });
  
  return {
    ...newState,
    history: newHistory
  };
}

// Helper function to clear stored state
const clearStoredState = () => {
  try {
    localStorage.removeItem(STORAGE_KEYS.APP_STATE);
    localStorage.removeItem(STORAGE_KEYS.MERGED_STRUCTURES);
    console.log('🗑️ STORED STATE CLEARED');
  } catch (error) {
    console.error('❌ Failed to clear stored state:', error);
  }
};

// Context provider
export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const [stage, setStage] = useState(null);
  const [canvas, setCanvas] = useState(null);

  // Register stage and canvas
  const registerStage = useCallback((stageInstance) => {
   
    setStage(stageInstance);
    if (stageInstance?._canvas) {
 
      setCanvas(stageInstance._canvas);
    } else {
    
    }
  }, []);

  // Get canvas coordinates
  const getCanvasCoordinates = useCallback((clientX, clientY) => {

    
    if (!stage || !canvas) {
      console.warn('Stage or canvas not available:', { stage, canvas });
      return null;
    }

    const rect = canvas.getBoundingClientRect();
    const scaleX = stage.app ? stage.app.screen.width / rect.width : 1;
    const scaleY = stage.app ? stage.app.screen.height / rect.height : 1;

    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    };
  }, [stage, canvas]);

  // Listen for removeShapes events from merge system
  useEffect(() => {
    const handleRemoveShapes = (event) => {
      const { shapeIds } = event.detail;
      dispatch({ type: ActionTypes.REMOVE_SHAPES, payload: { shapeIds } });
    };

    window.addEventListener('removeShapes', handleRemoveShapes);
    return () => window.removeEventListener('removeShapes', handleRemoveShapes);
  }, []);

  // Listen for merged structures updates from Canvas
  useEffect(() => {
    const handleMergedStructuresUpdate = (event) => {
      const { mergedStructures } = event.detail;
      dispatch({ type: 'SET_MERGED_STRUCTURES', payload: { mergedStructures } });
    };

    window.addEventListener('mergedStructuresUpdate', handleMergedStructuresUpdate);
    return () => window.removeEventListener('mergedStructuresUpdate', handleMergedStructuresUpdate);
  }, []);

  // Show notification if state was restored from storage
  useEffect(() => {
    if (state.shapes.length > 0 || (state.mergedStructures && state.mergedStructures.length > 0)) {
      const mergedStructuresWithComplexData = (state.mergedStructures || []).filter(ms => 
        ms.snappedSideLines?.length > 0 || 
        ms.gapFillerLines?.length > 0 || 
        Object.keys(ms.deletedSnappedLines || {}).length > 0 ||
        Object.keys(ms.deletedGapFillers || {}).length > 0
      );
      
      console.log('🎉 DRAWING RESTORED FROM STORAGE:', {
        shapesCount: state.shapes.length,
        mergedStructuresCount: state.mergedStructures ? state.mergedStructures.length : 0,
        complexMergedStructures: mergedStructuresWithComplexData.length,
        complexDataDetails: mergedStructuresWithComplexData.map(ms => ({
          id: ms.id,
          snappedLines: ms.snappedSideLines?.length || 0,
          gapFillers: ms.gapFillerLines?.length || 0,
          deletedTracking: Object.keys(ms.deletedSnappedLines || {}).length + Object.keys(ms.deletedGapFillers || {}).length
        })),
        lastSaved: state.lastSaved ? new Date(state.lastSaved).toLocaleString() : 'Unknown'
      });
      
      // Show user-friendly notification if there are complex merged structures
      if (mergedStructuresWithComplexData.length > 0) {
        setTimeout(() => {
          console.log('🏗️ MERGED STRUCTURES WITH COMPLEX DATA RESTORED:', {
            count: mergedStructuresWithComplexData.length,
            message: 'All snapped lines, gap fillers, and deletion tracking preserved!'
          });
        }, 1000);
      }
    }
  }, []); // Only run once on mount

  // Action creators
  const actions = {
    addShape: useCallback((shape) => {
      dispatch({ type: ActionTypes.ADD_SHAPE, payload: shape });
    }, []),
    
    updateShape: useCallback((id, updates) => {
   
      dispatch({ type: ActionTypes.UPDATE_SHAPE, payload: { id, updates } });
    }, [state]),
    
    updateShapes: useCallback((shapes) => {
      dispatch({ type: ActionTypes.UPDATE_SHAPES, payload: { shapes } });
    }, []),
    
    deleteShape: useCallback((id) => {
      console.log('🗑️ DELETE SHAPE CALLED:', {
        shapeId: id,
        selectedShapeId: state.selectedShapeId,
        totalShapes: state.shapes.length,
        shapeToDelete: state.shapes.find(s => s.id === id),
        mergedStructuresCount: state.mergedStructures ? state.mergedStructures.length : 0,
        timestamp: new Date().toLocaleTimeString()
      });
      
      // Check if trying to delete a merged structure
      if (state.mergedStructures) {
        const structureToDelete = state.mergedStructures.find(s => s.id === id);
        if (structureToDelete) {
          console.log('🏗️ DELETING MERGED STRUCTURE:', structureToDelete);
          // Delete merged structure instead of individual shape
          dispatch({ type: 'DELETE_MERGED_STRUCTURE', payload: { id } });
          return;
        }
      }
      
      dispatch({ type: ActionTypes.DELETE_SHAPE, payload: { id } });
    }, [state]),
    
    removeShapes: useCallback((shapeIds) => {
      dispatch({ type: ActionTypes.REMOVE_SHAPES, payload: { shapeIds } });
    }, []),
    
    selectShape: useCallback((id) => {
      dispatch({ type: ActionTypes.SELECT_SHAPE, payload: { id } });
    }, []),
    
    deselectAll: useCallback(() => {
      dispatch({ type: ActionTypes.DESELECT_ALL });
    }, []),
    
    mergeShapes: useCallback((shapeIds, mergedShape) => {
      dispatch({ type: ActionTypes.MERGE_SHAPES, payload: { shapeIds, mergedShape } });
    }, []),
    
    setTool: useCallback((tool) => {
      console.log('setTool called with:', tool);
      dispatch({ type: ActionTypes.SET_TOOL, payload: { tool } });
    }, []),
    
    undo: useCallback(() => {
      dispatch({ type: ActionTypes.UNDO });
    }, []),
    
    redo: useCallback(() => {
      dispatch({ type: ActionTypes.REDO });
    }, []),
    
    restoreState: useCallback((state) => {
      dispatch({ type: ActionTypes.RESTORE_STATE, payload: state });
    }, []),
    
    clearStoredState: useCallback(() => {
      clearStoredState();
    }, []),
    
    // Connection management actions
    addConnection: useCallback((shapeId1, nodeIndex1, shapeId2, nodeIndex2) => {
      console.log('🔗 ADD_CONNECTION called:', {
        shapeId1,
        nodeIndex1,
        shapeId2,
        nodeIndex2,
        currentConnectionsCount: state.connections.length
      });
      
      dispatch({ 
        type: ActionTypes.ADD_CONNECTION, 
        payload: { shapeId1, nodeIndex1, shapeId2, nodeIndex2 } 
      });
    }, []),
    
    removeConnection: useCallback((shapeId1, nodeIndex1, shapeId2, nodeIndex2) => {
      dispatch({ 
        type: ActionTypes.REMOVE_CONNECTION, 
        payload: { shapeId1, nodeIndex1, shapeId2, nodeIndex2 } 
      });
    }, []),
    
    updateConnections: useCallback((connections) => {
      dispatch({ 
        type: ActionTypes.UPDATE_CONNECTIONS, 
        payload: { connections } 
      });
    }, []),
    
    // Closed shape management actions
    addClosedShape: useCallback((lineIds, vertices, fillColor = '#e3f2fd') => {
      console.log('🎨 ADD_CLOSED_SHAPE called:', {
        lineIds,
        vertices,
        fillColor,
        currentClosedShapesCount: state.closedShapes.length
      });
      
      dispatch({ 
        type: ActionTypes.ADD_CLOSED_SHAPE, 
        payload: { lineIds, vertices, fillColor } 
      });
    }, []),
    
    updateClosedShape: useCallback((id, updates) => {
      dispatch({ 
        type: ActionTypes.UPDATE_CLOSED_SHAPE, 
        payload: { id, updates } 
      });
    }, []),
    
    removeClosedShape: useCallback((id) => {
      dispatch({ 
        type: ActionTypes.REMOVE_CLOSED_SHAPE, 
        payload: { id } 
      });
    }, []),
    
    clearAllClosedShapes: useCallback(() => {
      dispatch({ 
        type: ActionTypes.CLEAR_ALL_CLOSED_SHAPES
      });
    }, [])
  };
  
  const value = {
    ...state,
    ...actions,
    stage,
    canvas,
    registerStage,
    getCanvasCoordinates
  };
  
  // Debug logging for context value
  console.log('🔍 AppContext Provider Debug:', {
    stateKeys: Object.keys(state),
    actionsKeys: Object.keys(actions),
    hasAddConnection: typeof actions.addConnection === 'function',
    hasClearAllClosedShapes: typeof actions.clearAllClosedShapes === 'function',
    hasAddClosedShape: typeof actions.addClosedShape === 'function',
    valueKeys: Object.keys(value),
    valueHasAddConnection: typeof value.addConnection === 'function',
    valueHasClearAllClosedShapes: typeof value.clearAllClosedShapes === 'function',
    valueHasAddClosedShape: typeof value.addClosedShape === 'function'
  });
  
  return (
    <AppContext.Provider value={value} >
      {children}
    </AppContext.Provider>
  );
}

// Custom hook to use the app context
export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
} 